import { ExtrudeFeatureCommandParamsBuilder } from './ExtrudeFeatureCommandParamsBuilder';
import { ExtrudeFeatureDefinitionBuilder } from './ExtrudeFeatureDefinitionBuilder';
import { ExtrudeFeatureEditDefinitionDefaultBuilder } from './ExtrudeFeatureEditDefinitionDefaultBuilder';
import { ExtrudeFeatureEditDefinitionSanitizer } from './ExtrudeFeatureEditDefinitionSanitizer';

/**
 * 拉伸特征编辑定义。
 */
export interface ExtrudeFeatureEditDefinition {
    /** 当前特征语义类型。 */
    featureKind: ExtrudeFeatureKind;
    /** 当前特征操作类型。 */
    operation: ExtrudeOperation;
    /** 方向 1。 */
    direction1: ExtrudeDirectionDefinition;
    /** 方向 2。 */
    direction2: ExtrudeDirectionDefinition;
    /** 薄壁特征。 */
    thinFeature: ExtrudeThinFeatureDefinition;
    /** 是否合并结果。 */
    mergeResult: boolean;
    /** 所选轮廓输入框是否激活。 */
    selectedContoursInputActive: boolean;
    /** 拉伸切除专属定义。 */
    cutOptions: ExtrudeCutDefinition | null;
}

/**
 * 拉伸特征编辑定义 facade 工厂。
 *
 * 设计说明：
 * 1. 当前仓库已有大量调用点依赖该门面名称，不能直接移除；
 * 2. 具体实现职责已拆分到单一职责类，本类只做委托，避免继续膨胀成超级工厂；
 * 3. 这样既能维持兼容性，又能把后续扩展约束在清晰的职责边界内。
 */
export class ExtrudeFeatureEditDefinitionFactory {
    /**
     * 规范化拉伸编辑定义。
     *
     * @param definition 原始编辑定义。
     * @returns 纯数据编辑定义。
     */
    public static sanitizeDefinition(definition: ExtrudeFeatureEditDefinition): ExtrudeFeatureEditDefinition {
        return ExtrudeFeatureEditDefinitionSanitizer.sanitize(definition);
    }

    /**
     * 创建默认方向定义。
     *
     * @param overrides 覆盖项。
     * @returns 默认方向定义。
     */
    public static createDefaultDirection(
        overrides: Partial<ExtrudeDirectionDefinition> = {},
    ): ExtrudeDirectionDefinition {
        return ExtrudeFeatureEditDefinitionDefaultBuilder.createDefaultDirection(overrides);
    }

    /**
     * 创建默认薄壁定义。
     *
     * @returns 默认薄壁定义。
     */
    public static createDefaultThinFeature(): ExtrudeThinFeatureDefinition {
        return ExtrudeFeatureEditDefinitionDefaultBuilder.createDefaultThinFeature();
    }

    /**
     * 创建默认编辑定义。
     *
     * @param input 默认值构造输入。
     * @returns 默认编辑定义。
     */
    public static createDefault(input: {
        featureKind?: ExtrudeFeatureKind;
        operation?: ExtrudeOperation;
        cutOptions?: ExtrudeCutDefinition | null;
    } = {}): ExtrudeFeatureEditDefinition {
        return ExtrudeFeatureEditDefinitionDefaultBuilder.createDefault(input);
    }

    /**
     * 构建兼容旧执行链仍需使用的扁平载荷。
     *
     * @param definition 编辑定义。
     * @returns legacy 扁平载荷。
     */
    public static buildLegacyPayload(definition: ExtrudeFeatureEditDefinition): {
        depth: number;
        direction: 'normal' | 'reverse';
        startOffset: number;
    } {
        return ExtrudeFeatureCommandParamsBuilder.buildLegacyPayload(definition);
    }

    /**
     * 根据编辑定义构建可执行拉伸命令参数。
     *
     * @param baseParams 基础参数。
     * @param definition 编辑定义。
     * @returns 完整拉伸命令参数。
     */
    public static buildCommandParams(
        baseParams: Omit<
            ExtrudeParams,
            'depth' | 'direction' | 'startOffset' | 'mergeResult' | 'direction1' | 'direction2' | 'thinFeature'
        >,
        definition: ExtrudeFeatureEditDefinition,
    ): ExtrudeParams {
        return ExtrudeFeatureCommandParamsBuilder.buildCommandParams(baseParams, definition);
    }

    /**
     * 根据编辑定义直接构建拉伸特征定义对象。
     *
     * @param input 构建输入。
     * @returns 拉伸特征定义对象。
     */
    public static buildFeatureDefinition(input: {
        featureKind: ExtrudeFeatureKind;
        baseParams: Omit<
            ExtrudeParams,
            'depth' | 'direction' | 'startOffset' | 'mergeResult' | 'direction1' | 'direction2' | 'thinFeature'
        >;
        definition: ExtrudeFeatureEditDefinition;
    }) {
        return ExtrudeFeatureDefinitionBuilder.buildFeatureDefinition(input);
    }
}
